// src/components/agent/session/useChatList.js
//
// The live chat list (frontend spec §8.5 / §6.1.3): one onSnapshot over the
// newest CHAT_WINDOW chats by updated_at, filtered to platform == "decode" in
// memory (a where on platform plus an orderBy on updated_at would need a
// composite index, which the project bans), grouped into day buckets. Older
// chats are paged in as the sidebar is scrolled towards its end. Only chats
// leaving the limited live window need an existence check to distinguish a
// deletion from an ordinary move into older history.
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, orderBy, limit, startAfter, onSnapshot, getDocs, doc, getDocFromServer } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import useNow from '../../../hooks/useNow';
import { toMillis } from './partModel';
import { isTurnLive } from './useAgentSession';

// Over-fetched by a factor of two against the shown targets (100 / 50): the
// platform filter runs after the read.
const CHAT_WINDOW = 200;
const CHAT_PAGE = 100;

const rowFromDoc = (docSnap) => {
  const data = docSnap.data();
  if (data.platform !== 'decode') return null;
  const createdAtMs = toMillis(data.created_at);
  return {
    id: docSnap.id,
    chatName: data.chat_name || `Chat ${formatCreatedDate(createdAtMs)}`,
    activeTurn: data.active_turn || null,
    deleting: data.deleting === true,
    deletingClaimedMs: toMillis(data.deleting_claimed_at),
    sortMs: toMillis(data.updated_at) || createdAtMs,
  };
};

const formatCreatedDate = (createdAtMs) =>
  new Date(createdAtMs || Date.now()).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export default function useChatList(companyName) {
  const [windowChats, setWindowChats] = useState([]);
  // Chats before the live window, from pages and from the window letting go
  // of its oldest as newer activity arrived. Static copies: a rename or delete
  // made here patches them; activity elsewhere shows on the next reload.
  const [olderChats, setOlderChats] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);
  const windowRef = useRef(new Map());   // id → row in the last live snapshot
  const seenLiveIdsRef = useRef(new Set()); // A static page cannot overwrite live evidence.
  const deletedIdsRef = useRef(new Set());
  const historyReadsRef = useRef(new Map()); // id -> the current departure check
  const cursorRef = useRef(null);        // the oldest chat loaded so far
  const generationRef = useRef(0);       // bumped on every (re)subscribe; a page from before is dropped
  const [loading, setLoading] = useState(true);
  // The listener failed: the sidebar says so rather than "No chats yet". A failed
  // listener has stopped, so `retry` resubscribes.
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const generation = ++generationRef.current;
    setWindowChats([]);
    setOlderChats([]);
    setHasOlder(false);
    setLoadingOlder(false);
    loadingOlderRef.current = false;
    windowRef.current = new Map();
    seenLiveIdsRef.current = new Set();
    deletedIdsRef.current = new Set();
    historyReadsRef.current = new Map();
    cursorRef.current = null;
    setError(false);
    if (!companyName) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const agentsRef = collection(db, 'users', companyName, 'agents');
    const q = query(agentsRef, orderBy('updated_at', 'desc'), limit(CHAT_WINDOW));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (generationRef.current !== generation) return;
      const nextWindow = new Map();
      const rows = [];
      snapshot.docs.forEach((docSnap) => {
        const row = rowFromDoc(docSnap);
        nextWindow.set(docSnap.id, row);
        seenLiveIdsRef.current.add(docSnap.id);
        historyReadsRef.current.delete(docSnap.id);
        if (row && !deletedIdsRef.current.has(docSnap.id)) rows.push(row);
      });

      const departed = new Set();
      windowRef.current.forEach((row, id) => {
        if (!nextWindow.has(id) && row && !deletedIdsRef.current.has(id)) departed.add(id);
      });
      // Remove promoted and departed copies immediately, including while a
      // page or a departure check is still in flight.
      setOlderChats(previous => previous.filter(chat =>
        !nextWindow.has(chat.id) && !departed.has(chat.id) && !deletedIdsRef.current.has(chat.id)));

      for (const id of departed) {
        const check = Symbol('chat history lookup');
        historyReadsRef.current.set(id, check);
        getDocFromServer(doc(db, 'users', companyName, 'agents', id)).then(snapshot => {
          if (generationRef.current !== generation || historyReadsRef.current.get(id) !== check) return;
          historyReadsRef.current.delete(id);
          if (deletedIdsRef.current.has(id) || windowRef.current.has(id)) return;
          if (!snapshot.exists()) {
            deletedIdsRef.current.add(id);
            setOlderChats(previous => previous.filter(chat => chat.id !== id));
            return;
          }
          const row = rowFromDoc(snapshot);
          if (row) {
            setOlderChats(previous => {
              if (generationRef.current !== generation || deletedIdsRef.current.has(id)
                  || windowRef.current.has(id)) return previous;
              const byId = new Map(previous.map(chat => [chat.id, chat]));
              byId.set(id, row);
              return Array.from(byId.values());
            });
          }
        }).catch(error => {
          if (generationRef.current !== generation || historyReadsRef.current.get(id) !== check) return;
          console.error('Could not check a chat that left the live list:', error);
          setError(true);
        });
      }
      windowRef.current = nextWindow;

      if (!cursorRef.current && snapshot.docs.length) {
        cursorRef.current = snapshot.docs[snapshot.docs.length - 1];
        setHasOlder(snapshot.docs.length === CHAT_WINDOW);
      }
      setWindowChats(rows);
      setLoading(false);
    }, (listenError) => {
      console.error('Error listening to chat list:', listenError);
      setError(true);
      setLoading(false);
    });
    return () => {
      generationRef.current += 1;
      unsubscribe();
    };
  }, [companyName, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const loadOlder = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor || loadingOlderRef.current || !companyName) return;
    const generation = generationRef.current;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    try {
      const agentsRef = collection(db, 'users', companyName, 'agents');
      const snapshot = await getDocs(
        query(agentsRef, orderBy('updated_at', 'desc'), startAfter(cursor), limit(CHAT_PAGE))
      );
      if (generationRef.current !== generation) return;
      if (snapshot.docs.length) cursorRef.current = snapshot.docs[snapshot.docs.length - 1];
      const rows = snapshot.docs.map(rowFromDoc).filter(Boolean);
      setOlderChats(previous => {
        if (generationRef.current !== generation) return previous;
        const byId = new Map(previous.map(chat => [chat.id, chat]));
        for (const row of rows) {
          if (!seenLiveIdsRef.current.has(row.id) && !deletedIdsRef.current.has(row.id)) {
            byId.set(row.id, row);
          }
        }
        return Array.from(byId.values());
      });
      setHasOlder(snapshot.docs.length === CHAT_PAGE);
    } catch (loadError) {
      console.error('Error loading older chats:', loadError);
      if (generationRef.current === generation) setError(true);
    } finally {
      if (generationRef.current === generation) {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    }
  }, [companyName]);

  // Sidebar edits to a chat held in a static page.
  const patchChatName = useCallback((id, chatName) => {
    setOlderChats((prev) => prev.map((chat) => (chat.id === id ? { ...chat, chatName } : chat)));
  }, []);
  const forgetChat = useCallback((id) => {
    deletedIdsRef.current.add(id);
    historyReadsRef.current.delete(id);
    setWindowChats(previous => previous.filter(chat => chat.id !== id));
    setOlderChats((prev) => prev.filter((chat) => chat.id !== id));
  }, []);

  // The window wins over a static copy: a chat that saw activity after being
  // paged in has moved into the window with its live data.
  const chats = useMemo(() => {
    const byId = new Map();
    olderChats.forEach((chat) => byId.set(chat.id, chat));
    windowChats.forEach((chat) => byId.set(chat.id, chat));
    return Array.from(byId.values()).sort((a, b) => b.sortMs - a.sortMs);
  }, [olderChats, windowChats]);

  // Active-turn dots and the day buckets are both time-dependent — a crashed
  // turn's lease expires with no Firestore write, and a chat crosses into
  // Yesterday at midnight with none either — so both follow the shared clock.
  const now = useNow();

  // Day buckets: Today / Yesterday / Previous 7 days / Older. Boundaries are
  // local calendar days: yesterday is the previous local midnight, which on a
  // clock-change day is 23 or 25 hours ago, not 24.
  const groups = useMemo(() => {
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const todayMs = startOfToday.getTime();
    const yesterday = new Date(startOfToday);
    yesterday.setDate(startOfToday.getDate() - 1);
    const yesterdayMs = yesterday.getTime();
    const weekAgo = new Date(startOfToday);
    weekAgo.setDate(startOfToday.getDate() - 7);
    const weekAgoMs = weekAgo.getTime();

    const buckets = [
      { label: 'Today', chats: [] },
      { label: 'Yesterday', chats: [] },
      { label: 'Previous 7 days', chats: [] },
      { label: 'Older', chats: [] },
    ];
    for (const chat of chats) {
      const row = {
        ...chat,
        hasLiveTurn: isTurnLive(chat.activeTurn, now),
        deletionInProgress: chat.deleting && chat.deletingClaimedMs > now - 6 * 60 * 1000,
      };
      if (chat.sortMs >= todayMs) buckets[0].chats.push(row);
      else if (chat.sortMs >= yesterdayMs) buckets[1].chats.push(row);
      else if (chat.sortMs >= weekAgoMs) buckets[2].chats.push(row);
      else buckets[3].chats.push(row);
    }
    return buckets.filter((bucket) => bucket.chats.length > 0);
  }, [chats, now]);

  return { chats, groups, loading, error, retry, hasOlder, loadingOlder, loadOlder, patchChatName, forgetChat };
}
